import { Component, EventEmitter, inject, OnInit, Output } from '@angular/core';
import { ProjectService } from '../../../shared/services/project.service';
import { ProjectStage, PhaseModel, ProjectStatus, PROJECT_STATUS_OPTIONS } from '../../../shared/models/project.model';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { firstValueFrom } from 'rxjs';
import { TenantService } from '../../../shared/services/tenant.service';
import { Tenant } from '../../../shared/models/user.model';
import { DateInputsModule } from '@progress/kendo-angular-dateinputs';
import { PreventScrollPropagationDirective } from '../../../shared/directives/prevent-scroll-propagation.directive';

// Helper function to format date as YYYY-MM-DD in local timezone
function formatDateLocal(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// Extend ProjectStage with Date objects for Kendo DatePicker
interface ProjectStageWithDates extends Partial<ProjectStage> {
  startDateValue?: Date;
  endDateValue?: Date;
}

@Component({
  selector: 'app-add-project',
  imports: [FormsModule, CommonModule, DateInputsModule, PreventScrollPropagationDirective],
  templateUrl: './add-project.html'
})
export class AddProjectComponent implements OnInit {
  private projectService = inject(ProjectService);
  private tenantService = inject(TenantService);
  @Output() close = new EventEmitter<void>();
  isSubmitting = false;
  currentStep = 1; // 1 = Project Details, 2 = Phases

  canGoToStep2(): boolean {
    return !!this.projectData.projectName.trim() && !!this.projectData.tenantId;
  }

  goToStep2() {
    if (this.canGoToStep2()) this.currentStep = 2;
  }

  goToStep1() {
    this.currentStep = 1;
  }

  tenants: Tenant[] = [];

  // Date objects for Kendo DatePicker
  today: Date = new Date();
  projectStartDateValue: Date = new Date();

  readonly statusOptions = PROJECT_STATUS_OPTIONS;

  projectData: {
    projectName: string;
    startDate: string;
    tenantId: string;
    estimatedHours: number;
    status: ProjectStatus;
  } = {
      projectName: '',
      startDate: formatDateLocal(new Date()),
      tenantId: '',
      estimatedHours: 0,
      status: 'ACTIVE'
    };

  stages: ProjectStageWithDates[] = [];

  phaseModels: PhaseModel[] = [];
  selectedPhaseModelId?: string;

  // Default colors for stages
  defaultColors = ['#6366f1', '#8b5cf6', '#ec4899', '#f43f5e', '#f97316', '#eab308', '#22c55e', '#14b8a6'];

  async ngOnInit() {
    this.today.setHours(0, 0, 0, 0);
    this.projectStartDateValue = new Date(this.today);
    // Sync projectData.startDate with projectStartDateValue
    this.projectData.startDate = formatDateLocal(this.projectStartDateValue);

    // Now add the first stage with correct dates
    this.addStage();

    const phaseModels = await firstValueFrom(this.projectService.phaseModel$);
    this.phaseModels = phaseModels;
    this.tenants = await this.tenantService.listTenants();
    if (this.tenants.length > 0) {
      this.projectData.tenantId = this.tenants[0].id;
    }
  }

  // Handle project start date change from Kendo DatePicker
  onProjectStartDateChange(value: Date | null) {
    if (value) {
      this.projectStartDateValue = value;
      this.projectData.startDate = formatDateLocal(value);
      this.onProjectDateChange();
    }
  }

  // Called when project start date changes
  onProjectDateChange() {
    // Update all stages to ensure they respect the new project start date
    this.stages.forEach(stage => {
      // Update start date if it's before project start date OR if it's not set
      const stageStartTime = stage.startDateValue ? stage.startDateValue.getTime() : 0;
      const projectStartTime = this.projectStartDateValue.getTime();

      if (!stage.startDateValue || stageStartTime < projectStartTime) {
        // Use projectStartDateValue directly
        const newStartDate = new Date(this.projectStartDateValue);
        stage.startDate = formatDateLocal(newStartDate);
        stage.startDateValue = newStartDate;
      }

      // Update end date if it's before start date
      const stageEndTime = stage.endDateValue ? stage.endDateValue.getTime() : 0;
      const currentStartTime = stage.startDateValue ? stage.startDateValue.getTime() : projectStartTime;

      if (!stage.endDateValue || stageEndTime < currentStartTime) {
        const newEndDate = new Date(stage.startDateValue || this.projectStartDateValue);
        newEndDate.setDate(newEndDate.getDate() + 30);
        stage.endDate = formatDateLocal(newEndDate);
        stage.endDateValue = newEndDate;
      }
    });
  }

  addStage() {
    const colorIndex = this.stages.length % this.defaultColors.length;

    // Use projectStartDateValue directly to avoid timezone issues
    const startDate = new Date(this.projectStartDateValue);
    const defaultEndDate = new Date(this.projectStartDateValue);
    defaultEndDate.setDate(defaultEndDate.getDate() + 30);

    this.stages.push({
      stageName: '',
      startDate: formatDateLocal(startDate),
      endDate: formatDateLocal(defaultEndDate),
      color: this.defaultColors[colorIndex],
      status: 'NOTBEGIN',
      // Add Date objects for Kendo DatePicker
      startDateValue: startDate,
      endDateValue: defaultEndDate
    });
  }

  removeStage(index: number) {
    this.stages.splice(index, 1);
  }


  // Handle stage start date change
  onStageStartDateChange(index: number, value: Date | null) {
    if (value && this.stages[index]) {
      // Ensure start date is not before project start date
      if (value < this.projectStartDateValue) {
        value = this.projectStartDateValue;
      }

      this.stages[index].startDate = formatDateLocal(value);
      this.stages[index].startDateValue = value;

      // Update end date if it's before start date
      if (this.stages[index].endDate && this.stages[index].endDate! < this.stages[index].startDate!) {
        this.stages[index].endDate = this.stages[index].startDate;
        this.stages[index].endDateValue = new Date(value);
      }
    }
  }

  // Handle stage end date change
  onStageEndDateChange(index: number, value: Date | null) {
    if (value && this.stages[index]) {
      // Ensure end date is not before start date
      const minDate = this.stages[index].startDateValue || this.projectStartDateValue;
      if (value < minDate) {
        value = minDate;
      }

      this.stages[index].endDate = formatDateLocal(value);
      this.stages[index].endDateValue = value;
    }
  }

  onPhaseModelChange() {
    const model = this.phaseModels.find(m => m.id === this.selectedPhaseModelId);
    if (model) {
      // Sort phases by their defined order before mapping
      this.stages = model.phases
        .slice()
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map((phase, index) => {
          const startDate = new Date(this.projectStartDateValue);
          const endDate = new Date(this.projectStartDateValue);

          return {
            stageName: phase.name,
            color: phase.color || '#ffffff',
            startDate: formatDateLocal(startDate),
            endDate: formatDateLocal(endDate),
            status: 'NOTBEGIN',
            estimatedHours: phase.estimatedHours ?? 0,
            order: phase.order ?? index, // Use template order or fallback to index
            // Add Date objects for Kendo DatePicker
            startDateValue: startDate,
            endDateValue: endDate
          };
        });
    } else {
      this.stages = [];
      this.addStage();
    }
  }

  async onSubmit() {
    if (!this.projectData.tenantId) {
      alert('Please select a tenant. Create one in the Admin section if none exist.');
      return;
    }
    if (!this.projectData.projectName.trim()) {
      alert('Please enter a project name.');
      return;
    }

    this.isSubmitting = true;
    try {
      const newProject = await this.projectService.createProject(this.projectData);
      if (newProject && newProject.id) {
        const validStages = this.stages.filter(s => s.stageName && s.startDate && s.endDate);
        // Create stages with their order preserved from template
        const stagePromises = validStages.map((stage, index) =>
          this.projectService.createProjectStage({
            projectId: newProject.id,
            stageName: stage.stageName!,
            color: stage.color!,
            startDate: stage.startDate!,
            endDate: stage.endDate!,
            status: stage.status ?? 'NOTBEGIN',
            estimatedHours: stage.estimatedHours ?? 0,
            order: stage.order ?? index // Use template order or index
          })
        );
        await Promise.all(stagePromises);
      }
      this.projectData = { projectName: '', startDate: formatDateLocal(new Date()), tenantId: this.tenants[0]?.id || '', estimatedHours: 0, status: 'ACTIVE' };
      this.stages = [];
      this.selectedPhaseModelId = undefined;
      this.addStage();
      this.close.emit();
    } catch (err: any) {
      console.error("Error creating project:", err);
      alert(err?.message || "Error while creating project.");
    } finally {
      this.isSubmitting = false;
    }
  }
  getTotalPhaseHours(): number {
    return this.stages.reduce((sum, s) => sum + (s.estimatedHours || 0), 0);
  }

  isPhaseHoursOverBudget(): boolean {
    const budget = this.projectData.estimatedHours || 0;
    return budget > 0 && this.getTotalPhaseHours() > budget;
  }
}
